import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, GraduationCap, Rocket, Users,
  TrendingUp, Building2, Briefcase, BookOpen, Award, Brain,
  Target, Zap, ChevronRight, Play, Download, Star, Globe, MapPin,
} from "lucide-react";

const BOWER_LOGO = "https://customer-assets.emergentagent.com/job_9905ee30-c002-464f-b890-465ab6cc70dc/artifacts/9497g015_Bower%20School%20Logo-01.png";
const GITAM_LOGO = "https://customer-assets.emergentagent.com/job_43b23bc6-1c4f-424c-86a8-b69bfa82bcf1/artifacts/6tuycr6f_gitam-logo_light.svg";
const GITAM_LOGO_DARK = "https://customer-assets.emergentagent.com/job_43b23bc6-1c4f-424c-86a8-b69bfa82bcf1/artifacts/dcw1yx0v_gital-logo_dark.png";
const BROCHURE_PDF = "https://customer-assets.emergentagent.com/job_43b23bc6-1c4f-424c-86a8-b69bfa82bcf1/artifacts/80tqbl7f_GITAM%20Brochure%20%281%29%20%281%29.pdf";

const C = {
  teal: "#006659",
  tealLight: "#008F7A",
  tealDark: "#004D40",
  cream: "#F3F1E9",
  creamDark: "#E8E5D8",
  offWhite: "#F0EFE2",
  brown: "#8B6F47",
  accent: "#50A7B4",
  gold: "#D4A853",
};

// Google Drive photos
const GD = (id) => `https://lh3.googleusercontent.com/d/${id}=w800`;
const PHOTOS = {
  venture1: GD("1hWLp_0NCplUNPiycflEmh31qXesP_BYd"),
  awards: GD("1L0lN_LTgNsQongfdrqwhNGRUiYkAiSIK"),
  students1: GD("17didNt21yhYGGksmGCiEOyIqFi-rpiZK"),
  campus1: GD("1f0La9mnBAeqzB07BClfoI7hJDJ9UsdFS"),
  campus2: GD("133ZwLNZsl3QiINlDg01KYOevmwOIFyVh"),
  workshop1: GD("1g4bB_fjqBt8mI7nZOv1NJNYSfiXyi4RK"),
  workshop2: GD("1cjbZh1YeyjUJsrbTkcfdj5YdN9-0iwNk"),
  sports: GD("1NOf-XAZMpOYsUSaMIuFFP-u0TeMcikfu"),
  team: GD("1cajwl4YtxQ_agtpSwuZASBfDw6fYSSc6"),
  mentoring: GD("1sz-CVrna-haVnRRkDUTgiGu6BQBkmNCL"),
  portrait1: GD("1ERNLk18PYOJCfLKfpMr2FVHCSfeT3l_0"),
  dsc3023: GD("1cjbZh1YeyjUJsrbTkcfdj5YdN9-0iwNk"),
  dsc4278: GD("1g4bB_fjqBt8mI7nZOv1NJNYSfiXyi4RK"),
  z5b7815: GD("1hWLp_0NCplUNPiycflEmh31qXesP_BYd"),
};

const IMG = {
  heroBg: "https://static.prod-images.emergentagent.com/jobs/43b23bc6-1c4f-424c-86a8-b69bfa82bcf1/images/cf1824343587177c26aaf4e4b8fd3a74e33320846f8e2123f4dc96b62aa05c6e.png",
  ventureLab: "https://static.prod-images.emergentagent.com/jobs/43b23bc6-1c4f-424c-86a8-b69bfa82bcf1/images/d2f34ab232cb02210c181142d42d35ba41eb3b8a890d7e750a40b4769e3c1928.png",
  classroom: "https://static.prod-images.emergentagent.com/jobs/43b23bc6-1c4f-424c-86a8-b69bfa82bcf1/images/8ea09ca27a7d428e4e495b41da2bf8c5b8eac53f60e50be755f5bd4d9efc07cc.png",
  pitchDay: "https://static.prod-images.emergentagent.com/jobs/43b23bc6-1c4f-424c-86a8-b69bfa82bcf1/images/549de6d90a3aa446eb27bfc1442833423a866dd8f2a9046657c961caff03d0f1.png",
  gitamCampus: "https://apply.gitam.edu/images/applyloginimages/Hyderabad.webp",
  vdc: "https://apply.gitam.edu/images/applyloginimages/explore/new/VDC.webp",
  infra: "https://apply.gitam.edu/images/applyloginimages/explore/new/Infra.webp",
  // Faculty portraits (real)
  facultyAjit: "/people/ajit-rangnekar.jpg",
  facultySrinivas: "/people/srinivas-rao.jpg",
  facultySuresh: "/people/suresh-bhagavatula.jpg",
  // Student portraits (real)
  studentReshwanth: "/people/reshwanth.jpeg",
  studentSuga: "/people/sugavignesh.jpeg",
  studentZaid: "/people/zaid-khan.jpeg",
};

// Accreditation logos (local copies for reliability)
const ACCRED = {
  naac: "/logos/naac.png",
  nirf: "/logos/nirf.jpg",
  qs: "/logos/qs.png",
  the: "/logos/the.png",
  nba: "/logos/nba.png",
  ugc: "/logos/ugc.png",
};

const COUNTRY_CODES = [
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+44', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+971', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+65', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}' },
];

const INDIAN_STATES_CITIES = {
  "Andhra Pradesh": ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Tirupati"],
  "Bihar": ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur"],
  "Delhi": ["New Delhi", "South Delhi", "North Delhi", "East Delhi", "West Delhi"],
  "Gujarat": ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Gandhinagar"],
  "Haryana": ["Gurugram", "Faridabad", "Panipat", "Ambala", "Sonipat"],
  "Jharkhand": ["Ranchi", "Jamshedpur", "Dhanbad"],
  "Karnataka": ["Bengaluru", "Mysuru", "Mangaluru", "Hubli"],
  "Kerala": ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur"],
  "Madhya Pradesh": ["Bhopal", "Indore", "Jabalpur", "Gwalior"],
  "Maharashtra": ["Mumbai", "Pune", "Nagpur", "Thane", "Nashik"],
  "Odisha": ["Bhubaneswar", "Cuttack", "Rourkela"],
  "Punjab": ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Mohali"],
  "Rajasthan": ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Salem"],
  "Telangana": ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Agra", "Varanasi", "Noida", "Ghaziabad"],
  "Uttarakhand": ["Dehradun", "Haridwar", "Roorkee"],
  "West Bengal": ["Kolkata", "Howrah", "Durgapur", "Siliguri"],
  "Chhattisgarh": ["Raipur", "Bhilai", "Bilaspur"],
  "Goa": ["Panaji", "Margao", "Vasco da Gama"],
  "Chandigarh": ["Chandigarh"],
  "Jammu and Kashmir": ["Srinagar", "Jammu"],
};
const INDIAN_STATES = Object.keys(INDIAN_STATES_CITIES).sort();

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Zoho form action URL (updated formperma from user's latest Zoho export)
const ZOHO_FORM_ACTION = "https://forms.zohopublic.in/bowereducationalservicespriv1/form/GITAMBBAinEntrepreneurshipInterestFormLandingPage/formperma/6FQ84vZfPOk1pkc8pdq5XcKIPH_fD7zdszCSRw03KHs/htmlRecords/submit";

// ===== INTEREST FORM (submits via direct form POST to Zoho + backend backup) =====
const InterestForm = () => {
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", countryCode: "+91", phone: "",
    email: "", schoolName: "", state: "", city: "",
    grade: "", stream: "", board: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [availableCities, setAvailableCities] = useState([]);
  const zohoFormRef = useRef(null);

  useEffect(() => {
    if (formData.state && INDIAN_STATES_CITIES[formData.state]) {
      setAvailableCities(INDIAN_STATES_CITIES[formData.state]);
      setFormData(prev => ({ ...prev, city: "" }));
    } else { setAvailableCities([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.state]);

  const gradeOptions = ["-Select-", "Grade 11", "Grade 12"];
  const streamOptions = ["-Select-", "Science", "Commerce", "Arts/Humanities"];
  const boardOptions = ["-Select-", "ICSE", "CBSE", "IB", "State Board", "CAIE", "NIOS"];

  const validateForm = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = "Required";
    if (!formData.lastName.trim()) e.lastName = "Required";
    if (!formData.phone.trim()) e.phone = "Required";
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) e.phone = "Enter 10-digit number";
    if (!formData.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Invalid email";
    if (!formData.schoolName.trim()) e.schoolName = "Required";
    if (!formData.state) e.state = "Required";
    if (!formData.city) e.city = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    if (submitError) setSubmitError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError("");

    try {
      // Submit to Zoho via direct hidden form POST targeting a hidden iframe
      const zohoForm = zohoFormRef.current;
      if (!zohoForm) {
        setSubmitError("Form not ready. Please refresh and try again.");
        setIsSubmitting(false);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);

      // Set all hidden input values before submitting
      const setField = (name, val) => {
        const el = zohoForm.querySelector(`[name="${name}"]`);
        if (el) el.value = val;
      };

      setField("Name_First", formData.firstName);
      setField("Name_Last", formData.lastName);
      setField("PhoneNumber_countrycode", `${formData.countryCode}${formData.phone.replace(/\D/g, "")}`);
      setField("Email", formData.email);
      setField("SingleLine1", formData.schoolName);
      setField("Address_Region", formData.state);
      setField("Address_City", formData.city);
      setField("Dropdown1", formData.grade || "-Select-");
      setField("Dropdown", formData.stream || "-Select-");
      setField("Dropdown2", formData.board || "-Select-");
      setField("utm_source", urlParams.get("utm_source") || "");
      setField("utm_medium", urlParams.get("utm_medium") || "");
      setField("utm_campaign", urlParams.get("utm_campaign") || "");
      setField("utm_term", urlParams.get("utm_term") || "");
      setField("utm_content", urlParams.get("utm_content") || "");
      setField("zf_referrer_name", "GITAM_Landingpage");
      setField("referrer_name", "GITAM_Landingpage");

      // Direct form POST to Zoho (targets hidden iframe to avoid page navigation)
      zohoForm.submit();

      // Also save to backend as backup
      fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: formData.firstName, last_name: formData.lastName,
          phone: `${formData.countryCode}${formData.phone.replace(/\D/g, "")}`,
          email: formData.email, school_name: formData.schoolName,
          state: formData.state, city: formData.city,
          grade: formData.grade, stream: formData.stream, board: formData.board,
          referrer_name: "GITAM_Landingpage",
        }),
      }).catch(() => {});

      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
        if (window.fbq) window.fbq("track", "Lead", { content_name: "GITAM BBA Interest" });
      }, 2000);
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-10 px-4" data-testid="gitam-form-success">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: C.teal + "15" }}>
          <CheckCircle2 size={28} style={{ color: C.teal }} />
        </div>
        <h3 className="text-xl font-bold mb-2" style={{ color: C.tealDark }}>Application Received!</h3>
        <p className="text-base" style={{ color: "#777" }}>Our admissions team will reach out shortly.</p>
      </div>
    );
  }

  const inputCls = (err) => `w-full px-3 py-2.5 text-sm bg-white border rounded-lg outline-none transition-all placeholder-gray-400 focus:ring-2 focus:border-transparent ${err ? "border-red-400 focus:ring-red-200" : "border-gray-200 focus:ring-teal-200"}`;
  const selectCls = (err) => `w-full px-3 py-2.5 text-sm bg-white border rounded-lg outline-none transition-all appearance-none cursor-pointer focus:ring-2 focus:border-transparent ${err ? "border-red-400 focus:ring-red-200" : "border-gray-200 focus:ring-teal-200"}`;
  const labelCls = "block text-xs font-semibold uppercase tracking-wider mb-1";
  const errCls = "text-xs text-red-500 mt-0.5";

  return (
    <>
      {/* Hidden iframe target for Zoho form POST (avoids page navigation) */}
      <iframe
        name="zoho_submit_frame"
        style={{ position: "absolute", width: 0, height: 0, border: "none", overflow: "hidden" }}
        title="Zoho Submit Target"
        tabIndex={-1}
      />
      {/* Hidden Zoho form - direct POST to Zoho's endpoint via the hidden iframe */}
      <form
        ref={zohoFormRef}
        action={ZOHO_FORM_ACTION}
        method="POST"
        target="zoho_submit_frame"
        acceptCharset="UTF-8"
        encType="multipart/form-data"
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
        tabIndex={-1}
      >
        <input type="hidden" name="zf_referrer_name" value="" />
        <input type="hidden" name="zf_redirect_url" value="" />
        <input type="hidden" name="zc_gad" value="" />
        <input type="hidden" name="utm_source" value="" />
        <input type="hidden" name="utm_medium" value="" />
        <input type="hidden" name="utm_campaign" value="" />
        <input type="hidden" name="utm_term" value="" />
        <input type="hidden" name="utm_content" value="" />
        <input type="hidden" name="referrer_name" value="" />
        <input type="hidden" name="Name_First" value="" />
        <input type="hidden" name="Name_Last" value="" />
        <input type="hidden" name="PhoneNumber_countrycode" value="" />
        <input type="hidden" name="Email" value="" />
        <input type="hidden" name="SingleLine1" value="" />
        <input type="hidden" name="Address_Region" value="" />
        <input type="hidden" name="Address_City" value="" />
        <input type="hidden" name="Dropdown1" value="-Select-" />
        <input type="hidden" name="Dropdown" value="-Select-" />
        <input type="hidden" name="Dropdown2" value="-Select-" />
      </form>
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="gitam-interest-form">
      {submitError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" data-testid="gitam-form-error">
          {submitError}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls} style={{ color: C.tealDark }}>First Name <span className="text-red-500">*</span></label>
          <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First name" className={inputCls(errors.firstName)} data-testid="gitam-input-firstname" />
          {errors.firstName && <p className={errCls}>{errors.firstName}</p>}
        </div>
        <div>
          <label className={labelCls} style={{ color: C.tealDark }}>Last Name <span className="text-red-500">*</span></label>
          <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last name" className={inputCls(errors.lastName)} data-testid="gitam-input-lastname" />
          {errors.lastName && <p className={errCls}>{errors.lastName}</p>}
        </div>
      </div>
      <div>
        <label className={labelCls} style={{ color: C.tealDark }}>Phone <span className="text-red-500">*</span></label>
        <div className="flex gap-2">
          <select name="countryCode" value={formData.countryCode} onChange={handleChange} className="px-2 py-2.5 text-sm border border-gray-200 rounded-lg bg-white outline-none shrink-0 w-[88px]" data-testid="gitam-select-cc">
            {COUNTRY_CODES.map(cc => <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>)}
          </select>
          <input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="10-digit mobile" className={`flex-1 ${inputCls(errors.phone)}`} data-testid="gitam-input-phone" />
        </div>
        {errors.phone && <p className={errCls}>{errors.phone}</p>}
      </div>
      <div>
        <label className={labelCls} style={{ color: C.tealDark }}>Email <span className="text-red-500">*</span></label>
        <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className={inputCls(errors.email)} data-testid="gitam-input-email" />
        {errors.email && <p className={errCls}>{errors.email}</p>}
      </div>
      <div>
        <label className={labelCls} style={{ color: C.tealDark }}>School Name <span className="text-red-500">*</span></label>
        <input name="schoolName" value={formData.schoolName} onChange={handleChange} placeholder="Your school name" className={inputCls(errors.schoolName)} data-testid="gitam-input-school" />
        {errors.schoolName && <p className={errCls}>{errors.schoolName}</p>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls} style={{ color: C.tealDark }}>State <span className="text-red-500">*</span></label>
          <select name="state" value={formData.state} onChange={handleChange} className={selectCls(errors.state)} data-testid="gitam-select-state">
            <option value="">Select State</option>
            {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {errors.state && <p className={errCls}>{errors.state}</p>}
        </div>
        <div>
          <label className={labelCls} style={{ color: C.tealDark }}>City <span className="text-red-500">*</span></label>
          <select name="city" value={formData.city} onChange={handleChange} disabled={!formData.state} className={selectCls(errors.city)} style={{ opacity: formData.state ? 1 : 0.5 }} data-testid="gitam-select-city">
            <option value="">{formData.state ? "Select City" : "State first"}</option>
            {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {errors.city && <p className={errCls}>{errors.city}</p>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { name: "grade", label: "Grade", opts: gradeOptions, tid: "gitam-select-grade" },
          { name: "stream", label: "Stream", opts: streamOptions, tid: "gitam-select-stream" },
          { name: "board", label: "Board", opts: boardOptions, tid: "gitam-select-board" },
        ].map(f => (
          <div key={f.name}>
            <label className={labelCls} style={{ color: C.tealDark }}>{f.label}</label>
            <select name={f.name} value={formData[f.name]} onChange={handleChange} className={selectCls(false)} data-testid={f.tid}>
              {f.opts.map(o => <option key={o} value={o}>{o === "-Select-" ? "Select" : o}</option>)}
            </select>
          </div>
        ))}
      </div>
      <button type="submit" disabled={isSubmitting} className="w-full py-3.5 rounded-lg text-white font-bold text-base flex items-center justify-center gap-2 transition-all active:scale-[0.98] mt-1 hover:brightness-110" style={{ background: C.teal }} data-testid="gitam-form-submit-btn">
        {isSubmitting ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (<>Apply Now <ArrowRight size={16} /></>)}
      </button>
      <p className="text-xs text-center text-gray-400">
        By submitting, you agree to Bower's <a href="https://bowerschool.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>.
      </p>
    </form>
    </>
  );
};

// Section heading helper
const SectionHead = ({ tag, title, subtitle, light, center = true }) => (
  <div className={`${center ? "text-center" : ""} mb-10 md:mb-14`}>
    {tag && <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: light ? C.gold : C.accent }}>{tag}</span>}
    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight" style={{ color: light ? "#fff" : C.tealDark, fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
    {subtitle && <p className={`text-base md:text-lg mt-3 max-w-3xl ${center ? "mx-auto" : ""} ${light ? "text-white/60" : "text-gray-500"}`}>{subtitle}</p>}
  </div>
);

// ===== MAIN PAGE =====
const GitamBBAPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToForm = () => document.getElementById("gitam-form")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }} data-testid="gitam-bba-page">

      {/* ===== NAV ===== */}
      <nav className={`fixed top-0 w-full z-50 transition-all border-b ${isScrolled ? "bg-white/95 backdrop-blur-xl border-gray-100 shadow-sm" : "bg-white/60 backdrop-blur-xl border-transparent"}`}>
        <div className="flex justify-between items-center max-w-7xl mx-auto px-4 md:px-8 py-2.5">
          <div className="flex items-center gap-4">
            <Link to="/gitam" data-testid="gitam-logo-link">
              <img src={GITAM_LOGO_DARK} alt="GITAM" className="h-10 md:h-12 object-contain" />
            </Link>
            <div className="hidden md:flex items-center gap-2 text-sm font-medium text-gray-400">
              <span className="text-gray-500">Experiential Partner</span>
              <span className="mx-1 text-gray-300">&ndash;</span>
              <img src={BOWER_LOGO} alt="Bower" className="h-8 md:h-9 object-contain" />
            </div>
          </div>
          <div className="flex items-center gap-5">
            <a href={BROCHURE_PDF} target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center gap-1.5 text-sm font-semibold transition-colors hover:opacity-80" style={{ color: C.teal }} data-testid="nav-brochure-link">
              <Download size={14} /> Brochure
            </a>
            <Link to="/events" className="hidden md:block text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors">Events</Link>
            <button onClick={scrollToForm} className="px-6 py-2.5 rounded-full text-sm font-bold text-white transition-all hover:brightness-110 hover:scale-105" style={{ background: C.teal }} data-testid="gitam-nav-cta">
              Apply Now
            </button>
          </div>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative pt-20 overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.tealDark} 0%, ${C.teal} 50%, ${C.tealLight} 100%)` }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url(${IMG.heroBg})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-3 text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-6 border border-white/10">
                <span className="text-sm font-semibold text-white/90">BBA in Entrepreneurship</span>
                <span className="text-white/30">|</span>
                <span className="text-sm text-white/60">4-Year Programme</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Your Degree.<br />Your Startup.<br /><span style={{ color: C.gold }}>Together.</span>
              </h1>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-xl">
                A 4-year BBA from GITAM School of Business where you don't just study entrepreneurship &mdash; you live it. Build a real venture alongside your degree.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <button onClick={scrollToForm} className="px-8 py-3.5 rounded-full font-bold text-base transition-all hover:scale-105" style={{ background: C.gold, color: C.tealDark }} data-testid="hero-apply-btn">
                  Apply Now <ArrowRight className="inline ml-1" size={18} />
                </button>
                <a href={BROCHURE_PDF} target="_blank" rel="noopener noreferrer" className="px-8 py-3.5 rounded-full font-bold text-base border-2 border-white/30 text-white hover:bg-white/10 transition-all flex items-center gap-2" data-testid="hero-brochure-btn">
                  <Download size={18} /> Download Brochure
                </a>
              </div>
              {/* Quick stats */}
              <div className="flex flex-wrap gap-6 md:gap-10">
                {[
                  { val: "NAAC A++", label: "Accredited" },
                  { val: "AACSB", label: "Member" },
                  { val: "46+ Years", label: "Excellence" },
                  { val: "60 Seats", label: "Per Cohort" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.val}</div>
                    <div className="text-sm text-white/50 font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Form */}
            <div className="lg:col-span-2" id="gitam-form">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-white/20">
                <div className="p-4 text-center border-b" style={{ background: C.cream }}>
                  <h3 className="text-lg font-bold" style={{ color: C.tealDark }}>Register Your Interest</h3>
                  <p className="text-sm text-gray-400">GITAM School of Business &middot; Hyderabad Campus</p>
                </div>
                <div className="p-5"><InterestForm /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCREDITATION STRIP ===== */}
      <section className="py-5 border-b border-gray-100 bg-white" data-testid="accreditation-strip">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-center gap-4 md:gap-8 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-300">Recognised By</span>
            {[
              { text: "NAAC A++", sub: "Accredited" },
              { text: "AACSB", sub: "Member" },
              { text: "AMBA", sub: "Member" },
              { text: "NBA", sub: "Accredited" },
              { text: "UGC", sub: "#1 Category" },
              { text: "NIRF", sub: "Band 10-50" },
              { text: "THE", sub: "20th in India" },
              { text: "IIC", sub: "4-Star" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-100">
                <span className="text-sm font-bold" style={{ color: C.teal }}>{b.text}</span>
                <span className="text-[10px] text-gray-400 font-medium">{b.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DELIVERED JOINTLY ===== */}
      <section className="py-5 border-b" style={{ background: C.cream, borderColor: C.creamDark }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <span className="text-base md:text-lg font-semibold" style={{ color: C.brown }}>Delivered jointly on the</span>
            <img src={GITAM_LOGO_DARK} alt="GITAM" className="h-10 md:h-12 object-contain" />
            <span className="text-base md:text-lg font-semibold" style={{ color: C.brown }}>&amp;</span>
            <img src={BOWER_LOGO} alt="Bower" className="h-8 md:h-9 object-contain" />
            <span className="text-base md:text-lg font-semibold" style={{ color: C.brown }}>Hyderabad Campuses</span>
          </div>
        </div>
      </section>

      {/* ===== ABOUT THE PROGRAMME ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="The Programme" title="Three Pillars of Learning" subtitle="A unique curriculum that weaves academic rigour with real-world venture building, powered by India's most entrepreneurial ecosystem." />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <BookOpen size={28} />, title: "Academic Learning", desc: "122 credits across business fundamentals, finance, marketing, operations and multidisciplinary electives from GITAM's School of Business. Bloomberg Terminal & NSE certifications included.", img: PHOTOS.workshop1, color: C.teal },
              { icon: <Rocket size={28} />, title: "Venture Building", desc: "Build a real startup from Day 1. MVP development, legal setup, market validation, growth hacking, and fundraising. A continuous feedback loop between classroom and field reality.", img: PHOTOS.venture1, color: "#E07C3A" },
              { icon: <Users size={28} />, title: "Ecosystem Access", desc: "Insights on relevant funding opportunities, grants, and ecosystem events. Access to founders, investors, mentors, and Bower's partner school ecosystem across India.", img: PHOTOS.awards, color: C.accent },
            ].map((p, i) => (
              <div key={i} className="group rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all bg-white">
                <div className="h-52 overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-7">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: p.color + "12", color: p.color }}>{p.icon}</div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3" style={{ color: C.tealDark }}>{p.title}</h3>
                  <p className="text-base text-gray-500 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VENTURE BUILDING WITH BOWER ===== */}
      <section className="py-20 md:py-28" style={{ background: C.cream }} data-testid="venture-building-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <SectionHead tag="Experiential Partner" title="How Bower Powers Your Venture" subtitle="" center={false} />
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8">
                Bower School of Entrepreneurship brings 13 years of startup methodology to the classroom. While GITAM provides the academic foundation, Bower's Venture Development Centre (VDC) takes students from idea to investor-ready startup.
              </p>
              <div className="space-y-5">
                {[
                  { icon: <Target size={22} />, title: "Startup Lab 360", desc: "Semester 1 immersion - experience every role in a startup before building your own" },
                  { icon: <Zap size={22} />, title: "Venture Development Centre", desc: "Dedicated co-working space, prototyping lab, and mentor office hours on campus" },
                  { icon: <TrendingUp size={22} />, title: "Investor-Ready Pipeline", desc: "Pitch practice, demo days, angel network access, and accelerator partnerships" },
                  { icon: <Globe size={22} />, title: "Global Startup Exposure", desc: "Partner schools across 45+ institutions, startup ecosystem visits, and cross-campus collaborations" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: C.teal + "12", color: C.teal }}>{item.icon}</div>
                    <div>
                      <h4 className="text-lg font-bold" style={{ color: C.tealDark }}>{item.title}</h4>
                      <p className="text-base text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img src={IMG.ventureLab} alt="Venture Lab" className="w-full h-48 object-cover rounded-2xl" />
                <img src={PHOTOS.mentoring} alt="Mentoring session" className="w-full h-64 object-cover rounded-2xl" />
              </div>
              <div className="space-y-4 pt-8">
                <img src={IMG.pitchDay} alt="Pitch Day" className="w-full h-64 object-cover rounded-2xl" />
                <img src={PHOTOS.team} alt="Team collaboration" className="w-full h-48 object-cover rounded-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS - DUAL TRACK ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Dual Track Curriculum" title="Academic Rigour Meets Venture Reality" subtitle="Every semester, you advance both your degree and your startup." />
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl p-8 md:p-10 border border-gray-100 hover:shadow-lg transition-all bg-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: C.teal + "12" }}><GraduationCap size={24} style={{ color: C.teal }} /></div>
                <h3 className="text-2xl font-bold" style={{ color: C.tealDark }}>Academic Track</h3>
              </div>
              <p className="text-base text-gray-500 mb-5 leading-relaxed">GITAM School of Business delivers a rigorous BBA curriculum with 122 credits covering business strategy, finance, marketing, operations, and analytics.</p>
              <ul className="space-y-3">
                {["Business Analytics & Data Science", "Financial Accounting & Markets", "Marketing Management", "Bloomberg Terminal Certification", "NSE Financial Certification", "Multidisciplinary Electives"].map((t, i) => (
                  <li key={i} className="flex items-center gap-3 text-base text-gray-600"><CheckCircle2 size={18} style={{ color: C.teal }} />{t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl p-8 md:p-10 border-2 hover:shadow-lg transition-all" style={{ borderColor: C.teal + "30", background: C.teal + "04" }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "#E07C3A" + "15" }}><Rocket size={24} style={{ color: "#E07C3A" }} /></div>
                <h3 className="text-2xl font-bold" style={{ color: C.tealDark }}>Venture Track</h3>
              </div>
              <p className="text-base text-gray-500 mb-5 leading-relaxed">Bower's proprietary methodology takes you from ideation to investor-ready startup across 8 semesters of hands-on venture building.</p>
              <ul className="space-y-3">
                {["Startup Lab 360 Immersion", "MVP Development & Market Fit", "Legal Setup & IP Protection", "Growth Hacking & Distribution", "Fundraising & Investor Relations", "Demo Days & Pitch Practice"].map((t, i) => (
                  <li key={i} className="flex items-center gap-3 text-base text-gray-600"><Rocket size={18} style={{ color: "#E07C3A" }} />{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IMPACT STATS ===== */}
      <section className="py-16 md:py-20" style={{ background: C.tealDark }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
            {[
              { val: "200+", label: "Active Ventures" },
              { val: "50+", label: "Campus Startups" },
              { val: "13", label: "Years of Methodology" },
              { val: "45+", label: "Partner Schools" },
              { val: "60", label: "Seats per Cohort" },
            ].map((s, i) => (
              <div key={i} className="text-center py-4">
                <div className="text-3xl md:text-4xl font-black text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.val}</div>
                <div className="text-sm md:text-base text-white/50 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4-YEAR JOURNEY ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Curriculum Milestones" title="Your 4-Year Journey" subtitle="Each year builds on the last, taking you from explorer to founder." />
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { year: "Year 1", title: "Explore & Discover", color: C.teal, items: ["Startup Lab 360: Life of a Founder", "Ideation, Research & Prototype", "Feasibility & Business Model", "Venture Creation: Validate, Build, Acquire"] },
              { year: "Year 2", title: "Validate & Build", color: "#E07C3A", items: ["MVP Building & Legal Setup", "Market Fit & Customer Acquisition", "Community, Distribution & Growth", "Growth Experimentation"] },
              { year: "Year 3", title: "Launch & Ops", color: C.accent, items: ["Deep Ops & Team Dynamics", "Market Expansion", "Angel Investor Networking", "Startup Valuation & Pitch Refinement"] },
              { year: "Year 4", title: "Fundraise & Scale", color: C.brown, items: ["Competitive Strategy & Moats", "Investor Pitch & Negotiations", "Scaling Culture & Global Expansion", "M&A, IPO & Founder Liquidity"] },
            ].map((y, i) => (
              <div key={i} className="rounded-2xl p-7 border-2 border-gray-100 hover:shadow-lg transition-all bg-white relative">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5 text-white font-black text-lg" style={{ background: y.color, fontFamily: "'Outfit', sans-serif" }}>{i + 1}</div>
                <div className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: y.color }}>{y.year}</div>
                <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: C.tealDark }}>{y.title}</h3>
                <ul className="space-y-2.5">
                  {y.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-base text-gray-500">
                      <ChevronRight size={16} className="mt-1 shrink-0" style={{ color: y.color }} />{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CREDIT SYSTEM ===== */}
      <section className="py-16 md:py-20" style={{ background: C.cream }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Programme Structure" title="122-Credit System" subtitle="A balanced mix of academic rigour and practical venture building across 8 semesters." />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { credits: "22", label: "University Core", color: C.teal },
              { credits: "36", label: "Major Core", color: "#E07C3A" },
              { credits: "36", label: "Venture Lab / Major Elective-I", color: C.accent },
              { credits: "12", label: "Major Elective-II", color: C.brown },
              { credits: "12", label: "Multidisciplinary", color: C.gold },
              { credits: "4", label: "Internship", color: C.tealLight },
            ].map((c, i) => (
              <div key={i} className="text-center p-5 rounded-2xl bg-white border border-gray-100 hover:shadow-md transition-all">
                <div className="text-3xl font-black mb-1" style={{ color: c.color, fontFamily: "'Outfit', sans-serif" }}>{c.credits}</div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Credits</div>
                <div className="text-sm font-medium text-gray-700 mt-2">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CORE PROGRAMME COMPONENTS ===== */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Beyond the Classroom" title="Core Programme Components" subtitle="Learning extends far beyond lectures. Each component is designed to build real-world capability." />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {[
              { icon: <Brain size={24} />, title: "Masterclasses", desc: "Industry leaders share real-world insights and case studies from the frontlines of business." },
              { icon: <Users size={24} />, title: "Ecosystem Events", desc: "Demo Days, startup fairs, networking sessions with founders, investors, and CXOs." },
              { icon: <Zap size={24} />, title: "Workshops", desc: "Hands-on skill-building in design thinking, financial modeling, pitch craft, and product management." },
              { icon: <Rocket size={24} />, title: "Venture Labs", desc: "Dedicated co-working spaces and prototyping labs where your startup idea becomes reality." },
              { icon: <MapPin size={24} />, title: "Immersions & Field Visits", desc: "On-ground visits to startups, incubators, factories, and ecosystem hubs across India." },
              { icon: <Briefcase size={24} />, title: "Internship Exposure", desc: "Structured internships with startups, corporates, and social enterprises for hands-on experience." },
            ].map((c, i) => (
              <div key={i} className="p-6 rounded-2xl border border-gray-100 hover:shadow-md transition-all bg-white">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: C.teal + "12", color: C.teal }}>{c.icon}</div>
                <h4 className="text-lg font-bold mb-2" style={{ color: C.tealDark }}>{c.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MULTIDISCIPLINARY ELECTIVES ===== */}
      <section className="py-16 md:py-20" style={{ background: C.cream }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Choose Your Edge" title="Multidisciplinary Electives" subtitle="Pick 12 credits from across GITAM's departments to build a unique skill stack that gives your venture an unfair advantage." />
          <div className="flex flex-wrap justify-center gap-3">
            {["Data Science", "Information Technology", "Bioinformatics", "Biochemistry", "Biotechnology", "Environmental Science", "Food Science & Technology", "Statistics", "Electronics", "Economics", "Psychology", "Sociology", "Political Science", "Mass Communication", "Visual Communication"].map((e, i) => (
              <span key={i} className="px-5 py-2.5 rounded-full text-sm font-medium border border-gray-200 bg-white hover:shadow-md transition-all cursor-default" style={{ color: C.tealDark }}>
                {e}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHO THIS PROGRAMME IS FOR ===== */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Is This You?" title="Who This Programme Is For" />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Aspiring Entrepreneurs", desc: "You have ideas and the drive to build something of your own. You want to launch a startup while earning a degree.", icon: <Rocket size={24} /> },
              { title: "Future Business Leaders", desc: "You want a business degree that goes beyond theory — one that teaches you to think, build, and lead.", icon: <Target size={24} /> },
              { title: "Family Business Successors", desc: "You're preparing to take over or transform your family enterprise with modern strategy and innovation.", icon: <Building2 size={24} /> },
              { title: "Creative Problem-Solvers", desc: "You see problems as opportunities. You want a programme that values action over memorisation.", icon: <Brain size={24} /> },
            ].map((p, i) => (
              <div key={i} className="p-7 rounded-2xl border border-gray-100 hover:shadow-lg transition-all text-center bg-white">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: C.teal + "12", color: C.teal }}>{p.icon}</div>
                <h4 className="text-lg font-bold mb-2" style={{ color: C.tealDark }}>{p.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WEEKLY SCHEDULE ===== */}
      <section className="py-16 md:py-20" style={{ background: C.tealDark }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="A Week in the Life" title="Typical Weekly Schedule" light />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { day: "Mon", activity: "Academic Classes", sub: "Business fundamentals, finance, analytics", color: C.teal },
              { day: "Tue", activity: "Venture Labs", sub: "Work on your startup with mentor support", color: "#E07C3A" },
              { day: "Wed", activity: "Masterclasses", sub: "Industry leaders & ecosystem events", color: C.accent },
              { day: "Thu", activity: "Academic Classes", sub: "Marketing, operations, strategy", color: C.teal },
              { day: "Fri", activity: "Workshops & Immersions", sub: "Skill-building & field visits", color: C.gold },
            ].map((d, i) => (
              <div key={i} className="p-5 rounded-2xl text-center border border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: d.color }}>{d.day}</div>
                <div className="text-base font-bold text-white mb-1">{d.activity}</div>
                <div className="text-xs text-white/40">{d.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY GITAM ===== */}
      <section className="py-20 md:py-28" style={{ background: C.tealDark }} data-testid="why-gitam-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="By the Numbers" title="Why GITAM?" subtitle="Every metric tells the same story - real outcomes, real careers." light />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { val: "3,728", label: "Placement Offers", sub: "Last hiring cycle" },
              { val: "\u20B91.4 Cr", label: "Highest Package", sub: "Top offer by Amazon" },
              { val: "1,800+", label: "Expert Faculty", sub: "95%+ PhDs" },
              { val: "42%", label: "On Scholarship", sub: "Merit awards up to 100%" },
            ].map((s, i) => (
              <div key={i} className="text-center p-6 md:p-8 rounded-2xl border border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.val}</div>
                <div className="text-base md:text-lg font-semibold text-white/70 mb-1">{s.label}</div>
                <div className="text-xs text-white/35 uppercase tracking-wider">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PHOTO GALLERY ===== */}
      <section className="overflow-hidden">
        <div className="flex gap-1">
          {[PHOTOS.sports, IMG.gitamCampus, IMG.pitchDay, PHOTOS.venture1, IMG.infra, PHOTOS.awards].map((src, i) => (
            <div key={i} className="flex-1 min-w-[16%] h-48 md:h-64 overflow-hidden">
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* ===== ACCREDITATIONS DETAILED ===== */}
      <section className="py-16 md:py-20 bg-white border-b border-gray-100" data-testid="accreditations-detailed">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Recognised by India's Finest" title="Accreditations & Rankings" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { img: ACCRED.naac, label: "NAAC A++", sub: "Highest Accreditation Grade", badge: "A++" },
              { img: null, label: "AACSB Member", sub: "Business Education Alliance", badge: "AACSB", icon: <Award size={24} /> },
              { img: null, label: "AMBA Member", sub: "MBA Accreditation Body", badge: "AMBA", icon: <Award size={24} /> },
              { img: ACCRED.nba, label: "NBA Accredited", sub: "National Board of Accreditation", badge: "NBA" },
              { img: ACCRED.ugc, label: "UGC #1 Category", sub: "Autonomous University", badge: "UGC" },
              { img: null, label: "NIRF Ranked", sub: "Innovation Band 10-50, Mgmt 101-125", badge: "NIRF", icon: <Star size={24} /> },
              { img: ACCRED.the, label: "THE 801+", sub: "20th in India", badge: "THE" },
              { img: null, label: "IIC 4-Star", sub: "Innovation Council Rating", badge: "IIC", icon: <Star size={24} /> },
            ].map((a, i) => (
              <div key={i} className="flex flex-col items-center text-center p-6 md:p-7 rounded-2xl border border-gray-100 hover:shadow-lg transition-all bg-white" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}>
                {a.img ? (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 border-2" style={{ borderColor: C.teal + "30", background: C.teal + "08" }}>
                    <img src={a.img} alt={a.label} className="h-8 w-8 object-contain" />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: C.teal + "12", color: C.teal }}>{a.icon}</div>
                )}
                <div className="text-xl font-bold mb-1" style={{ color: C.tealDark }}>{a.label}</div>
                <div className="text-sm text-gray-400 mt-1">{a.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CAREER PATHWAYS ===== */}
      <section className="py-20 md:py-28" style={{ background: C.tealDark }} data-testid="career-pathways">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="After Graduation" title="Career Pathways" subtitle="Multiple routes to success - whether you build, join, or transform." light />
          <div className="grid md:grid-cols-4 gap-6 mb-10">
            {[
              { title: "Build Your Venture", desc: "Launch your own startup with seed funding, mentorship, and investor access from the Bower ecosystem.", icon: <Rocket size={24} />, color: C.gold },
              { title: "Family Business", desc: "Transform your family enterprise with modern business strategy, digital operations, and growth frameworks.", icon: <Building2 size={24} />, color: "#E07C3A" },
              { title: "Corporate Placements", desc: "Join leading companies with a unique founder's mindset. GITAM's 3,728 offers last cycle open doors globally.", icon: <Briefcase size={24} />, color: C.accent },
              { title: "Early-Stage Roles", desc: "Join high-growth startups as a founding team member. Your venture experience makes you invaluable.", icon: <TrendingUp size={24} />, color: "#7CC47F" },
            ].map((p, i) => (
              <div key={i} className="p-7 rounded-2xl border border-white/10 hover:border-white/20 transition-all" style={{ background: "rgba(255,255,255,0.05)" }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: p.color + "20", color: p.color }}>{p.icon}</div>
                <h4 className="text-xl font-bold text-white mb-3">{p.title}</h4>
                <p className="text-base text-white/50 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="p-6 rounded-2xl border border-white/10" style={{ background: "rgba(255,255,255,0.04)" }}>
            <h4 className="text-sm font-bold mb-3 text-center uppercase tracking-wider text-white/50">Top Recruiters at GITAM</h4>
            <p className="text-lg text-white/40 text-center leading-relaxed">
              Federal Bank &middot; Jio &middot; Colgate-Palmolive &middot; PwC &middot; Thomson Reuters &middot; Oracle &middot; Deloitte &middot; TCS &middot; KPMG &middot; Kellogg's &middot; Oxane Partners &middot; Perfetti van Melle
            </p>
          </div>
        </div>
      </section>

      {/* ===== QUOTES ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Voices" title="From the Leadership" />
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {[
              { name: "Prof. Raja P Pappu", role: "Dean, GITAM School of Business", quote: "This programme is a bold reimagining of business education in India. By integrating academic rigour with real venture building, we are creating graduates who don't just understand business, they build businesses.", color: C.teal, img: null },
              { name: "Pavan Allena", role: "Chief Problem Solver, Bower School", quote: "At Bower, we believe the best way to learn entrepreneurship is to do it. This programme gives students the rare opportunity to build a real startup while earning a world-class degree from GITAM.", color: "#E07C3A", img: null },
            ].map((q, i) => (
              <div key={i} className="p-8 md:p-10 rounded-2xl border border-gray-100 hover:shadow-lg transition-all bg-white" style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
                <div className="text-4xl mb-4" style={{ color: q.color, fontFamily: "Georgia, serif" }}>&ldquo;</div>
                <p className="text-lg text-gray-600 leading-relaxed italic mb-6">{q.quote}</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ background: q.color }}>
                    {q.name.split(" ").slice(-1)[0][0]}
                  </div>
                  <div>
                    <div className="text-base font-bold" style={{ color: C.tealDark }}>{q.name}</div>
                    <div className="text-sm" style={{ color: q.color }}>{q.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Advisory Board */}
          <SectionHead tag="Advisory Board" title="Guided by India's Best" subtitle="Leaders from academia, industry, and the startup ecosystem who shape the programme's vision." />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Mr. Ajit Rangnekar", role: "Ex-Dean, ISB", quote: "A programme that combines the best of GITAM's academic tradition with Bower's entrepreneurial energy.", color: C.teal, img: IMG.facultyAjit },
              { name: "Mr. Srinivas Rao Mahankali (MSR)", role: "Ex-CEO, T-Hub", quote: "T-Hub showed me the power of ecosystems. This programme builds that same ecosystem thinking into undergraduate education.", color: "#E07C3A", img: IMG.facultySrinivas },
              { name: "Mr. Suresh Bhagavatula", role: "Professor, IIM Bangalore", quote: "This programme bridges the gap between academic knowledge and real-world venture creation in a way few programmes can.", color: C.accent, img: IMG.facultySuresh },
            ].map((f, i) => (
              <div key={i} className="p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all bg-white" style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
                <img src={f.img} alt={f.name} className="w-20 h-20 rounded-full object-cover mb-5 border-3" style={{ borderColor: f.color + "30" }} />
                <h4 className="text-xl font-bold mb-1" style={{ color: C.tealDark }}>{f.name}</h4>
                <p className="text-sm font-medium mb-4" style={{ color: f.color }}>{f.role}</p>
                <p className="text-base text-gray-500 leading-relaxed italic">"{f.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ===== STUDENT SUCCESS ===== */}
      <section className="py-20 md:py-28" style={{ background: C.cream }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Success Stories" title="Students Building Real Ventures" subtitle="Our students don't wait to graduate. They launch, learn, and lead from Day 1." />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Reshwanth S", venture: "ReBrick", desc: "Sustainability and construction venture — exploring innovative building materials with lower environmental impact.", img: IMG.studentReshwanth, color: C.teal },
              { name: "Suga Vignesh", venture: "Trovo", desc: "D2C footwear brand — building a direct-to-consumer footwear label combining design, comfort, and affordability.", img: IMG.studentSuga, color: "#E07C3A" },
              { name: "Zaid Khan", venture: "VIP School", desc: "Institutional and family business management — creating systems to professionalise and scale family-run educational institutions.", img: IMG.studentZaid, color: C.accent },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-gray-100 bg-white hover:shadow-lg transition-all" style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
                <div className="h-56 overflow-hidden">
                  <img src={s.img} alt={s.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-7">
                  <div className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-3" style={{ background: s.color }}>{s.venture}</div>
                  <h4 className="text-xl font-bold mb-2" style={{ color: C.tealDark }}>{s.name}</h4>
                  <p className="text-base text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FACULTY DOMAINS ===== */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Expert Faculty From" title="Faculty Domains" subtitle="Learn from practitioners and academics across the full spectrum of business and entrepreneurship." />
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {["VC & PE", "Corporate Law & IP", "Product Design & UX", "EdTech", "Enterprise Risk & Finance", "Brand & Growth Marketing", "Ops & Supply Chain", "Academia", "Angel Investing", "Startup Ecosystems"].map((d, i) => (
              <div key={i} className="text-center p-4 rounded-xl border border-gray-100 bg-white hover:shadow-md transition-all">
                <span className="text-sm font-semibold" style={{ color: C.tealDark }}>{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STUDENT CLUBS ===== */}
      <section className="py-16 md:py-20" style={{ background: C.cream }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Campus Life" title="Student Clubs at GITAM" subtitle="Beyond academics, GITAM's vibrant club ecosystem builds leadership, creativity, and community." />
          <div className="flex flex-wrap justify-center gap-3">
            {["Alikya", "Finlords", "ISBC", "Sanskriti", "NSS", "Logiway", "Prerana", "AlmaKonect", "MarkSoc", "Paragan"].map((club, i) => (
              <span key={i} className="px-5 py-2.5 rounded-full text-sm font-semibold border-2 bg-white hover:shadow-md transition-all" style={{ borderColor: C.teal + "30", color: C.teal }}>
                {club}
              </span>
            ))}
          </div>
        </div>
      </section>


      {/* ===== ELIGIBILITY & FEE ===== */}
      <section className="py-20 md:py-28" style={{ background: C.cream }} data-testid="eligibility-fee">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="Admissions" title="Eligibility & Fee Structure" />
          <div className="grid md:grid-cols-2 gap-8">
            {/* Eligibility */}
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm">
              <h3 className="text-2xl font-bold mb-6" style={{ color: C.tealDark }}>Who Can Apply?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3"><CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: C.teal }} /><span className="text-base text-gray-600">Minimum <strong>50% in 10+2</strong> or equivalent (any discipline)</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: C.teal }} /><span className="text-base text-gray-600">Qualified in <strong>GITAM Admission Test (GAT)</strong></span></li>
                <li className="flex items-start gap-3"><CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: C.teal }} /><span className="text-base text-gray-600"><strong>Personal Interview</strong> with the admissions panel</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 size={20} className="mt-0.5 shrink-0" style={{ color: C.teal }} /><span className="text-base text-gray-600">Open to <strong>all streams</strong> &mdash; Science, Commerce, Humanities</span></li>
              </ul>
              <div className="mt-6 p-4 rounded-xl" style={{ background: C.teal + "08" }}>
                <p className="text-sm text-gray-500"><strong style={{ color: C.teal }}>Pro tip:</strong> Students who clear BEAT (Bower Entrepreneurial Aptitude Test) get priority consideration and scholarship eligibility.</p>
              </div>
            </div>
            {/* Fee */}
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm">
              <h3 className="text-2xl font-bold mb-6" style={{ color: C.tealDark }}>Fee Structure</h3>
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr style={{ background: C.teal + "08" }}>
                      <th className="text-left p-4 text-sm font-bold uppercase tracking-wider" style={{ color: C.teal }}>Year</th>
                      <th className="text-right p-4 text-sm font-bold uppercase tracking-wider" style={{ color: C.teal }}>Tuition Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { year: "Year 1", fee: "\u20B98,00,000" },
                      { year: "Year 2", fee: "\u20B98,40,000" },
                      { year: "Year 3", fee: "\u20B98,82,000" },
                      { year: "Year 4", fee: "\u20B99,26,000" },
                    ].map((f, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="p-4 text-base font-medium text-gray-700">{f.year}</td>
                        <td className="p-4 text-right text-lg font-bold" style={{ color: C.tealDark }}>{f.fee}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2" style={{ borderColor: C.teal + "20" }}>
                      <td className="p-4 text-base font-bold" style={{ color: C.tealDark }}>Total</td>
                      <td className="p-4 text-right text-xl font-black" style={{ color: C.teal }}>{"\u20B9"}34,48,000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-400 mt-3">*Tuition fees increase by 5-8% in subsequent years. 3% discount available for full annual payment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ADMISSION PROCESS ===== */}
      <section className="py-20 md:py-28 bg-white" data-testid="admission-process">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHead tag="How to Apply" title="Admission Process" />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0 relative">
            <div className="hidden md:block absolute top-1/2 left-[10%] right-[10%] h-0.5 -translate-y-1/2" style={{ background: `linear-gradient(to right, ${C.teal}30, ${C.teal}30)` }} />
            {[
              { step: "1", label: "Online Application", desc: "Fill the interest form above", icon: "edit_note" },
              { step: "2", label: "GITAM Admission Test", desc: "Take the GAT exam", icon: "quiz" },
              { step: "3", label: "Personal Interview", desc: "With the admissions panel", icon: "groups" },
              { step: "4", label: "Admission Confirmed", desc: "Secure your seat", icon: "verified" },
            ].map((s, i) => (
              <div key={i} className="flex md:flex-col items-center md:items-center gap-4 md:gap-3 relative z-10 flex-1 md:text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white shadow-lg" style={{ background: C.teal }}>
                  <span className="material-symbols-outlined text-2xl md:text-3xl">{s.icon}</span>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.accent }}>Step {s.step}</div>
                  <div className="text-lg md:text-xl font-bold" style={{ color: C.tealDark }}>{s.label}</div>
                  <div className="text-sm md:text-base text-gray-400">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* GAT Exam Pattern */}
          <div className="mt-20">
            <SectionHead tag="Exam Details" title="GAT Exam Pattern" subtitle="The GITAM Admission Test (GAT) for BBA consists of objective-type questions. Each correct answer earns 2 marks with no negative marking." />
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                { section: "Critical Thinking", questions: 30, marks: 60, time: "45 min", color: C.teal },
                { section: "Problem Solving", questions: 30, marks: 60, time: "45 min", color: "#E07C3A" },
                { section: "Language Comprehension", questions: 40, marks: 80, time: "60 min", color: C.accent },
              ].map((s, i) => (
                <div key={i} className="p-7 rounded-2xl border border-gray-100 bg-white text-center hover:shadow-md transition-all">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: s.color + "12" }}>
                    <span className="text-2xl font-black" style={{ color: s.color }}>{s.questions}</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-1">{s.section}</div>
                  <div className="text-base text-gray-400">{s.marks} marks &middot; {s.time}</div>
                </div>
              ))}
            </div>
            <div className="text-center text-base text-gray-500">
              Total: <strong className="text-gray-700">100 Questions</strong> &middot; <strong className="text-gray-700">200 Marks</strong> &middot; <strong className="text-gray-700">150 Minutes</strong> &middot; Up to 3 attempts per cycle
            </div>
          </div>

          {/* Scholarship Table */}
          <div className="mt-20">
            <SectionHead tag="Merit Scholarships" title="Scholarship Based on GAT Score" />
            <div className="max-w-3xl mx-auto rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <table className="w-full">
                <thead>
                  <tr style={{ background: C.teal + "08" }}>
                    <th className="text-left p-5 text-sm font-bold uppercase tracking-wider" style={{ color: C.teal }}>GAT Score</th>
                    <th className="text-center p-5 text-sm font-bold uppercase tracking-wider" style={{ color: C.teal }}>Scholarship</th>
                    <th className="text-right p-5 text-sm font-bold uppercase tracking-wider" style={{ color: C.teal }}>Benefit</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { score: "90% and above", waiver: "100% Waiver", benefit: "Full tuition fee waived" },
                    { score: "80% - 89.99%", waiver: "75% Waiver", benefit: "75% of tuition fee waived" },
                    { score: "70% - 79.99%", waiver: "50% Waiver", benefit: "50% of tuition fee waived" },
                    { score: "60% - 69.99%", waiver: "25% Waiver", benefit: "25% of tuition fee waived" },
                  ].map((r, i) => (
                    <tr key={i} className="border-t border-gray-50">
                      <td className="p-5 text-base text-gray-700 font-medium">{r.score}</td>
                      <td className="p-5 text-center text-lg font-bold" style={{ color: C.teal }}>{r.waiver}</td>
                      <td className="p-5 text-right text-base text-gray-500">{r.benefit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 text-sm text-gray-400" style={{ background: C.cream }}>*Scholarship is renewable with CGPA 8.0+. Offered on a first-come, first-served basis subject to seat availability.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="py-4 px-4 md:px-8" style={{ background: C.cream }}>
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.tealDark}, ${C.teal})` }}>
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url(${IMG.heroBg})`, backgroundSize: "cover" }} />
            <div className="relative z-10 p-12 md:p-20 text-center">
              <div className="flex items-center justify-center gap-5 mb-8">
                <img src={GITAM_LOGO} alt="GITAM" className="h-12 md:h-14 object-contain" />
                <div className="h-10 w-px bg-white/20" />
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/50 font-medium">Experiential Partner</span>
                  <img src={BOWER_LOGO} alt="Bower" className="h-10 md:h-11 object-contain brightness-0 invert" />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Ready to Build Your Future?</h2>
              <p className="text-lg md:text-xl text-white/60 mb-8 max-w-2xl mx-auto">Join a programme where your classmates are co-founders, your campus is a launchpad, and your degree is a competitive edge.</p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <button onClick={scrollToForm} className="px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105" style={{ background: C.gold, color: C.tealDark }} data-testid="cta-apply-btn">
                  Apply Now <ArrowRight className="inline ml-1" size={20} />
                </button>
                <a href={BROCHURE_PDF} target="_blank" rel="noopener noreferrer" className="px-10 py-4 rounded-full font-bold text-lg border-2 border-white/30 text-white hover:bg-white/10 transition-all flex items-center justify-center gap-2" data-testid="cta-brochure-btn">
                  <Download size={20} /> Download Brochure
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-16 px-4 md:px-8 text-white" style={{ background: C.tealDark }} data-testid="gitam-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src={GITAM_LOGO} alt="GITAM" className="h-12 object-contain" />
              </div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-white/40 font-medium uppercase tracking-wider">Experiential Partner</span>
                <img src={BOWER_LOGO} alt="Bower" className="h-10 object-contain brightness-0 invert" />
              </div>
              <p className="text-sm text-white/40 leading-relaxed">Building the next generation of industry disruptors through experiential entrepreneurship education.</p>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">Programme</h4>
              <ul className="space-y-2">
                <li><a href="#" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-base text-white/40 hover:text-white transition-colors">BBA in Entrepreneurship</a></li>
                <li><a href="/gitam" className="text-base text-white/40 hover:text-white transition-colors">Curriculum</a></li>
                <li><a href={BROCHURE_PDF} target="_blank" rel="noopener noreferrer" className="text-base text-white/40 hover:text-white transition-colors flex items-center gap-1"><Download size={12} /> Download Brochure</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/events" className="text-base text-white/40 hover:text-white transition-colors">Events</Link></li>
                <li><Link to="/seed" className="text-base text-white/40 hover:text-white transition-colors">SEED Partnerships</Link></li>
                <li><a href="https://bowerschool.com" target="_blank" rel="noopener noreferrer" className="text-base text-white/40 hover:text-white transition-colors">Bower School</a></li>
                <li><a href="https://gitam.edu" target="_blank" rel="noopener noreferrer" className="text-base text-white/40 hover:text-white transition-colors">GITAM University</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">Contact</h4>
              <ul className="space-y-2">
                <li className="text-base text-white/40 flex items-start gap-2"><MapPin size={16} className="mt-1 shrink-0" />GITAM Hyderabad Campus, Rudraram, Telangana 502329</li>
                <li className="text-base text-white/40 flex items-start gap-2"><MapPin size={16} className="mt-1 shrink-0" />Bower: 8th Floor, T-Hub, Knowledge City Rd, Hyderabad 500032</li>
                <li><a href="tel:9063172305" className="text-base text-white/40 hover:text-white transition-colors">Phone: 9063172305</a></li>
                <li><a href="mailto:admissions@bowerschool.com" className="text-base text-white/40 hover:text-white transition-colors">admissions@bowerschool.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/25">&copy; {new Date().getFullYear()} GITAM (Deemed to be University). All rights reserved.</p>
            <div className="flex gap-6">
              <a href="https://bowerschool.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm text-white/25 hover:text-white/50 transition-colors">Privacy Policy</a>
              <a href="https://bowerschool.com/terms" target="_blank" rel="noopener noreferrer" className="text-sm text-white/25 hover:text-white/50 transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default GitamBBAPage;
